/**
 * PATH: apps/billing/src/lib/billsClient.ts
 * FILE_ID: BILLING-LIB-BILLSCLIENT-001
 * PURPOSE: Axios adapter satisfying `@quotech/bills`'s `BillsClient` interface.
 *
 * WHY: The 3-pane workspace in `@quotech/bills` is transport-agnostic.
 *      It calls `useBillsClient()` from a React context. Each app must
 *      provide its own implementation. This file is apps/billing's.
 *
 *      Phase A's `useBills.ts` hooks already make the same calls via
 *      TanStack Query — that path stays for the existing pages
 *      (BillsPage, DashboardPage, etc.). The workspace uses this client
 *      directly so it doesn't depend on TanStack at the package level.
 *
 * FLOW:
 *   <BillsProvider client={createAxiosBillsClient()}>
 *     └─ <BookkeepingWorkspace/>          (in @quotech/bills)
 *          └─ useBillsClient().list(...)  -> getDefaultClient().get(...)
 */
import DOMPurify from 'dompurify';
import {
  type BillsClient,
  type BillDetail,
  type BillEditablePatch,
  type BillEmailDetail,
  type BillPoAllocation,
  type BillPoAllocationsResponse,
  type BillPoSuggestionsResponse,
  type BillsListResponse,
  type BillStats,
  type BillStatus,
  type BillStatusFilter,
  type BillFlag,
  type BillListOrder,
  type ConvertEmailToPoInput,
  type CreateBillPoAllocationInput,
  type CreatePoPaymentInput,
  type CreatePurchaseOrderInput,
  type ExtractPendingResponse,
  type PaymentRequestsResponse,
  type PoPayment,
  type PoPaymentsResponse,
  type PurchaseOrderDetail,
  type PurchaseOrdersListResponse,
  type UpdatePurchaseOrderInput,
  type XeroPaymentSuggestionsResponse,
  type SetSupplierTermInput,
  type MarkBillPaidInput,
  type BillPaymentSuggestionsResponse,
  type ChartOfAccountsResponse,
  billsEndpoints,
  normalizeSupplierList,
} from '@quotech/bills';
import { getDefaultClient } from '@quotech/api';

export function createAxiosBillsClient(): BillsClient {
  const client = getDefaultClient();

  return {
    async list(
      filter: {
        status?: BillStatusFilter;
        q?: string;
        flag?: BillFlag;
        order?: BillListOrder;
        limit?: number;
        offset?: number;
      } = {},
    ) {
      const url = billsEndpoints.list({
        status: filter.status,
        q: filter.q,
        flag: filter.flag,
        order: filter.order,
        limit: filter.limit,
        offset: filter.offset,
      });
      const { data } = await client.get<BillsListResponse>(url);
      return data;
    },

    async detail(billId) {
      const { data } = await client.get<BillDetail>(billsEndpoints.detail(billId));
      return data;
    },

    async update(billId, patch: BillEditablePatch) {
      const { data } = await client.patch<BillDetail>(billsEndpoints.update(billId), patch);
      return data;
    },

    async stats() {
      const { data } = await client.get<BillStats>(billsEndpoints.stats());
      return data;
    },

    async getChartOfAccounts(opts = {}) {
      const { data } = await client.get<ChartOfAccountsResponse>(
        billsEndpoints.chartOfAccounts(opts),
      );
      return data;
    },

    async listSuppliers(search) {
      const { data } = await client.get<unknown>(billsEndpoints.suppliers(search));
      return normalizeSupplierList(data);
    },

    async approve(billId, opts) {
      const { data } = await client.post<BillDetail>(
        billsEndpoints.approve(billId),
        opts?.force ? { force: true } : undefined,
      );
      return data;
    },

    async reject(billId, reason) {
      const { data } = await client.post<BillDetail>(billsEndpoints.reject(billId), {
        reason: reason ?? '',
      });
      return data;
    },

    async linkJob(billId, jobId) {
      const { data } = await client.post<BillDetail>(billsEndpoints.linkJob(billId), {
        job_id: jobId,
      });
      return data;
    },

    async pushToXero(billId, opts) {
      const { data } = await client.post<BillDetail>(
        billsEndpoints.pushToXero(billId),
        opts?.force ? { force: true } : undefined,
      );
      return data;
    },

    async setSupplierTerm(billId, input: SetSupplierTermInput) {
      const { data } = await client.post<BillDetail>(
        billsEndpoints.supplierTerm(billId),
        input,
      );
      return data;
    },

    async markPaid(billId, input: MarkBillPaidInput) {
      const { data } = await client.post<BillDetail>(
        billsEndpoints.markPaid(billId),
        input,
      );
      return data;
    },

    async getBillPaymentSuggestions(billId) {
      const { data } = await client.get<BillPaymentSuggestionsResponse>(
        billsEndpoints.paymentSuggestions(billId),
      );
      return data;
    },

    async extractFromEmail(emailId) {
      const { data } = await client.post<BillDetail>(billsEndpoints.extractFromEmail(emailId));
      return data;
    },

    async extractPending(opts = {}) {
      const { data } = await client.post<ExtractPendingResponse>(
        billsEndpoints.extractPending(),
        opts,
      );
      return data;
    },

    async setEmailCategory(emailId, category) {
      await client.post(billsEndpoints.emailBulkAction(), {
        email_ids: [emailId],
        action: 'set_category',
        category,
        locked: true,
      });
    },

    /**
     * Backend route (verified in repos/quotech-backend/organisation/urls.py):
     *   path('api/email-attachments/<int:attachment_id>/download/', ...)
     *   path('api/email-attachments/<uuid:attachment_id>/download/', ...)
     *
     * The serializer emits `attachment_id` directly from the DB pk —
     * which can be int OR uuid depending on org migration history.
     * Django dispatches to the right view based on the URL pattern,
     * so we don't need to inspect the type here.
     *
     * Auth: this returns a fully-qualified URL so the browser fetches
     * it from the backend host. The session cookie carries auth (the
     * backend sets `SameSite=None; Secure` for cross-subdomain auth).
     */
    attachmentUrl(attachmentId) {
      if (!attachmentId) return null;
      const base = client.defaults.baseURL?.replace(/\/$/, '') || '';
      return `${base}/api/email-attachments/${attachmentId}/download/`;
    },

    /**
     * HTML sanitiser for the BillEmailPanel body view. Mirrors
     * apps/sam — plain DOMPurify defaults, which strip <script>,
     * on-* handlers, javascript: URIs, and other XSS vectors while
     * preserving benign tags like <p>, <a>, <table>, signatures, etc.
     *
     * Without this, the package-side BillEmailPanel falls back to
     * plain-text — which loses tables, lists, and inline images.
     * jake 2026-05-05 explicitly asked for the rich body inline.
     */
    sanitiseHtml(html) {
      return DOMPurify.sanitize(html);
    },

    /**
     * Source email for the bill, mapped onto the package's narrower
     * BillEmailDetail contract.
     */
    async fetchEmailDetail(emailId): Promise<BillEmailDetail | null> {
      if (!emailId) return null;
      const { data } = await client.get<{
        id: string;
        subject: string;
        from_address: string;
        from_name?: string;
        to_addresses?: Array<{ name?: string; email?: string }>;
        cc_addresses?: Array<{ name?: string; email?: string }>;
        body_text?: string;
        body_html?: string;
        body_preview?: string;
        received_at: string;
        attachments?: Array<{
          id?: string | number;
          filename?: string;
          content_type?: string;
          size?: number;
        }>;
      }>(`/api/emails/${emailId}/`);
      return {
        id: data.id,
        subject: data.subject ?? '',
        from_address: data.from_address ?? '',
        from_name: data.from_name,
        to_addresses: data.to_addresses,
        cc_addresses: data.cc_addresses,
        body_text: data.body_text,
        body_html: data.body_html,
        body_preview: data.body_preview,
        received_at: data.received_at,
        attachments: data.attachments,
      };
    },

    /* ────────────────────────────────────────────────────────────────
     * Purchase orders + bill ↔ PO allocation (PR-1, 2026-05-05)
     *
     * Backend: organisation/views/po_views/  (see endpoints.ts).
     * Wired so the right-rail "PO" tab in apps/billing's bookkeeping
     * workspace gets the same allocation + reconciliation UX as SAM.
     * ──────────────────────────────────────────────────────────────── */

    async listPurchaseOrdersForJob(jobId, opts) {
      const { data } = await client.get<PurchaseOrdersListResponse>(
        billsEndpoints.poListForJob(jobId, opts),
      );
      return data;
    },

    async listPurchaseOrders(filter) {
      const { data } = await client.get<PurchaseOrdersListResponse>(
        billsEndpoints.poList(filter),
      );
      return data;
    },

    async createPurchaseOrder(input: CreatePurchaseOrderInput) {
      const { data } = await client.post<PurchaseOrderDetail>(
        billsEndpoints.poCreate(),
        input,
      );
      return data;
    },

    async updatePurchaseOrder(poId, patch: UpdatePurchaseOrderInput) {
      const { data } = await client.patch<PurchaseOrderDetail>(
        billsEndpoints.poDetail(poId),
        patch,
      );
      return data;
    },

    async deletePurchaseOrder(poId) {
      await client.delete(billsEndpoints.poDetail(poId));
    },

    async listBillPoAllocations(billId) {
      const { data } = await client.get<BillPoAllocationsResponse>(
        billsEndpoints.billPoAllocations(billId),
      );
      return data;
    },

    async upsertBillPoAllocation(billId, input: CreateBillPoAllocationInput) {
      const { data } = await client.post<BillPoAllocation>(
        billsEndpoints.billPoAllocationCreate(billId),
        input,
      );
      return data;
    },

    async deleteBillPoAllocation(billId, allocationId) {
      await client.delete(billsEndpoints.billPoAllocationDetail(billId, allocationId));
    },

    async getBillPoSuggestions(billId) {
      const { data } = await client.get<BillPoSuggestionsResponse>(
        billsEndpoints.billPoSuggestions(billId),
      );
      return data;
    },

    /* ────────────────────────────────────────────────────────────────
     * PO payments + convert-to-PO + payment-requests inbox  (PR-2)
     * ──────────────────────────────────────────────────────────────── */

    async listPoPayments(poId) {
      const { data } = await client.get<PoPaymentsResponse>(
        billsEndpoints.poPayments(poId),
      );
      return data;
    },

    async recordPoPayment(poId, input: CreatePoPaymentInput) {
      const { data } = await client.post<{
        payment: PoPayment;
        amount_paid: string;
        payment_status: string;
      }>(billsEndpoints.poPayments(poId), input);
      return data;
    },

    async deletePoPayment(poId, paymentId) {
      const { data } = await client.delete<{
        amount_paid: string;
        payment_status: string;
      }>(billsEndpoints.poPaymentDetail(poId, paymentId));
      return data;
    },

    async getPoPaymentSuggestions(poId) {
      const { data } = await client.get<XeroPaymentSuggestionsResponse>(
        billsEndpoints.poPaymentSuggestions(poId),
      );
      return data;
    },

    async convertEmailToPo(emailId, input: ConvertEmailToPoInput) {
      const { data } = await client.post<PurchaseOrderDetail>(
        billsEndpoints.emailConvertToPo(emailId),
        input,
      );
      return data;
    },

    async listPaymentRequests(opts = {}) {
      const { data } = await client.get<PaymentRequestsResponse>(
        billsEndpoints.paymentRequests({
          include_quotes: opts.include_quotes,
        }),
      );
      return data;
    },
  };
}
